import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, Input } from 'reactstrap';
import { Button, Badge, Table, Field, TextInput, Select, Loader, EmptyState, DescriptionList } from '../../../../ui';
import { StatTile, TeacherStatusBadge } from './ui-pending';
import './leaveDetails.scss';
import { leaveBalanceV2, leaveDetails, addLeaveBalanceH1H2, calculateNumberOfLeaves, applyTeacherLeave } from '../../../../Api/apiUtils';
import { formatDate } from '../../../../utils/formatDate';
import { toast } from 'react-toastify';
import { MixpanelEvents, trackEvent } from '../../../../utils/mixpanelTracking';

interface LeaveHistoryEntry {
  leaveYear: string;
  h1Balance: number;
  h1LeavesAvailed: number;
  h1leavesRemaining: number;
  h2Balance: number;
  h2LeavesAvailed: number;
  h2leavesRemaining: number;
}

const parseApiDate = (dateStr: string): Date => {
  if (!dateStr) return new Date();
  const parts = String(dateStr).trim().split(/[-/]/);
  if (parts.length >= 3) {
    const d = new Date(parts[2] + '-' + parts[1] + '-' + parts[0]);
    if (!isNaN(d.getTime())) return d;
  }
  return new Date(dateStr);
};

/** Handles flat or wrapped bodies, e.g. { totalLeaves, isEmergencySlot } or { data: { ... } }. */
function extractLeaveCount(res: unknown): number | null {
  if (res == null) return null;
  if (typeof res === "number" && Number.isFinite(res)) return res;
  if (typeof res === "string" && res.trim() !== "" && !Number.isNaN(Number(res))) return Number(res);
  const priorityKeys = ["totalLeaves", "leaveDuration", "numberOfLeaves", "noOfDays"] as const;

  const readFromObject = (o: Record<string, unknown>): number | null => {
    for (const k of priorityKeys) {
      if (!(k in o)) continue;
      const v = o[k];
      if (typeof v === "number" && Number.isFinite(v)) return v;
      if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v))) return Number(v);
    }
    const pascalRaw = o["TotalLeaves"];
    const pascal =
      typeof pascalRaw === "number" && Number.isFinite(pascalRaw)
        ? pascalRaw
        : typeof pascalRaw === "string" && Number.isFinite(Number(pascalRaw))
          ? Number(pascalRaw)
          : null;
    return pascal;
  };

  let cur: unknown = res;
  const seen = new Set<unknown>();
  for (let d = 0; d < 8 && cur != null && typeof cur === "object"; d++) {
    if (seen.has(cur)) break;
    seen.add(cur);
    const found = readFromObject(cur as Record<string, unknown>);
    if (found != null) return found;
    const o = cur as Record<string, unknown>;
    const next = o.data ?? o.result ?? o.payload ?? o.content ?? o.body;
    cur = next;
  }

  return null;
}

function formatLeaveCountCell(val: unknown): string | number {
  const n = extractLeaveCount(val);
  if (n != null) return n;
  if (val != null && typeof val !== "object") return String(val);
  return "-";
}

const LEAVE_TYPES = ['Casual'];

const TIME_OPTIONS = (() => {
  const opts: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      opts.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`);
    }
  }
  return opts;
})();

const LeaveDetails: React.FC<{ teacherId: string }> = ({ teacherId }) => {
  const [leaveBalanceData, setLeaveBalanceData] = useState<{
    totalBalance?: number;
    leavesRemaining?: number;
    leavesAvailed?: number;
    leavehistory?: LeaveHistoryEntry[];
    currentDate?: string;
  } | null>(null);

  const [leaveDetailsData, setLeaveDetailsData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [updateModalYear, setUpdateModalYear] = useState('');
  const [formData, setFormData] = useState({
    h1balance: '',
    h1leaveRemaining: '',
    h1leaveAvailed: '',
    h2balance: '',
    h2leaveRemaining: '',
    h2leaveAvailed: ''
  });
  const [applyForm, setApplyForm] = useState({
    leaveType: 'Casual',
    fromDate: '',
    fromTime: '',
    toDate: '',
    toTime: '',
    reason: ''
  });
  const [noOfDays, setNoOfDays] = useState<number | null>(null);
  const [calculatingDays, setCalculatingDays] = useState(false);
  const [submittingApply, setSubmittingApply] = useState(false);

  const currentDate = leaveBalanceData?.currentDate ? parseApiDate(leaveBalanceData.currentDate) : new Date();
  const isMidYearOrLater = currentDate.getMonth() >= 6;
  const history = leaveBalanceData?.leavehistory ?? [];
  const has2027 = history.some((h: LeaveHistoryEntry) => String(h.leaveYear) === '2027');
  const displayYear = isMidYearOrLater && has2027 ? '2027' : '2026';
  const displayEntry = history.find((h: LeaveHistoryEntry) => String(h.leaveYear) === displayYear) ?? null;

  // Fetch leave balance data
  const fetchLeaveBalance = async () => {
    setIsLoading(true);
    try {
      const response = await leaveBalanceV2(teacherId);
      setLeaveBalanceData(response);
    } catch (error) {
      console.error('Error fetching leave balance:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openUpdateModal = () => {
    setUpdateModalYear(displayYear);
    const entry = history.find((h: LeaveHistoryEntry) => String(h.leaveYear) === displayYear) ?? null;
    if (entry) {
      setFormData({
        h1balance: String(entry.h1Balance ?? ''),
        h1leaveRemaining: String(entry.h1leavesRemaining ?? ''),
        h1leaveAvailed: String(entry.h1LeavesAvailed ?? ''),
        h2balance: String(entry.h2Balance ?? ''),
        h2leaveRemaining: String(entry.h2leavesRemaining ?? ''),
        h2leaveAvailed: String(entry.h2LeavesAvailed ?? '')
      });
    } else {
      setFormData({
        h1balance: '', h1leaveRemaining: '', h1leaveAvailed: '',
        h2balance: '', h2leaveRemaining: '', h2leaveAvailed: ''
      });
    }
    setIsModalOpen(true);
  };

  const availableYears = history.length > 0
    ? history.map((h: LeaveHistoryEntry) => String(h.leaveYear)).filter(Boolean)
    : [displayYear];

  const handleUpdateModalYearChange = (year: string) => {
    setUpdateModalYear(year);
    const entry = history.find((h: LeaveHistoryEntry) => String(h.leaveYear) === year) ?? null;
    if (entry) {
      setFormData({
        h1balance: String(entry.h1Balance ?? ''),
        h1leaveRemaining: String(entry.h1leavesRemaining ?? ''),
        h1leaveAvailed: String(entry.h1LeavesAvailed ?? ''),
        h2balance: String(entry.h2Balance ?? ''),
        h2leaveRemaining: String(entry.h2leavesRemaining ?? ''),
        h2leaveAvailed: String(entry.h2LeavesAvailed ?? '')
      });
    }
  };

  const handleAddLeave = async () => {
    if (!updateModalYear) {
      toast.error('Please select a year.');
      return;
    }
    const h1b = Number(formData.h1balance);
    const h1r = Number(formData.h1leaveRemaining);
    const h1a = Number(formData.h1leaveAvailed);
    const h2b = Number(formData.h2balance);
    const h2r = Number(formData.h2leaveRemaining);
    const h2a = Number(formData.h2leaveAvailed);
    if ([h1b, h1r, h1a, h2b, h2r, h2a].some((n) => isNaN(n) || n < 0)) {
      toast.error('Please enter valid numbers for all fields.');
      return;
    }
    try {
      await addLeaveBalanceH1H2({
        teacherId,
        leaveYear: updateModalYear,
        h1balance: h1b,
        h1leaveRemaining: h1r,
        h1leaveAvailed: h1a,
        h2balance: h2b,
        h2leaveRemaining: h2r,
        h2leaveAvailed: h2a
      });
      toast.success('Leave balance updated successfully.');
      trackEvent(MixpanelEvents.TEACHER_LEAVE_BALANCE_UPDATED, {
        teacherId,
        leaveYear: updateModalYear,
        h1balance: h1b,
        h1leaveRemaining: h1r,
        h1leaveAvailed: h1a,
        h2balance: h2b,
        h2leaveRemaining: h2r,
        h2leaveAvailed: h2a,
      });
      fetchLeaveBalance();
      fetchLeaveDetails();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error updating leave balance:', error);
      toast.error('Failed to update leave balance.');
    }
  };
  const fetchLeaveDetails = async () => {
    setIsLoading(true);
    try {
      const response = await leaveDetails(teacherId);
      setLeaveDetailsData(response.content || []);
    } catch (error) {
      console.error('Error fetching leave details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveBalance();
    fetchLeaveDetails();
  }, [teacherId]);

  const fetchNoOfDays = useCallback(async () => {
    const { fromDate, toDate, fromTime, toTime } = applyForm;
    if (!fromDate || !toDate || !fromTime || !toTime || !teacherId) {
      setNoOfDays(null);
      return;
    }
    setCalculatingDays(true);
    try {
      const res = await calculateNumberOfLeaves({
        teacherId: Number(teacherId),
        fromDate,
        toDate,
        fromTime,
        toTime
      });
      const total = extractLeaveCount(res);
      setNoOfDays(total != null ? total : 0);
    } catch {
      setNoOfDays(null);
    } finally {
      setCalculatingDays(false);
    }
  }, [applyForm.fromDate, applyForm.toDate, applyForm.fromTime, applyForm.toTime, teacherId]);

  useEffect(() => {
    fetchNoOfDays();
  }, [fetchNoOfDays]);

  const handleApplyLeave = async () => {
    const { fromDate, toDate, fromTime, toTime, reason } = applyForm;
    if (!fromDate || !toDate || !fromTime || !toTime || !reason.trim()) {
      toast.error('Please fill From, To, and Reason.');
      return;
    }
    if (noOfDays == null) {
      toast.error('Please wait for leave duration to be calculated or check dates.');
      return;
    }
    setSubmittingApply(true);
    try {
      await applyTeacherLeave({
        teacherId: Number(teacherId),
        fromDate,
        toDate,
        fromTime,
        toTime,
        reason: reason.trim(),
        leaveDuration: noOfDays
      });
      toast.success('Leave applied successfully.');
      trackEvent(MixpanelEvents.TEACHER_LEAVE_APPLIED, {
        teacherId: Number(teacherId),
        fromDate,
        toDate,
        fromTime,
        toTime,
        reason: reason.trim(),
        leaveDuration: noOfDays,
      });
      setIsApplyModalOpen(false);
      setApplyForm({ leaveType: 'Casual', fromDate: '', fromTime: '', toDate: '', toTime: '', reason: '' });
      setNoOfDays(null);
      fetchLeaveBalance();
      fetchLeaveDetails();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to apply leave.');
    } finally {
      setSubmittingApply(false);
    }
  };

  return (
    <div className="tp-leave">
      <div className="tp-leave__toolbar">
        <Button size="sm" variant="secondary" onClick={openUpdateModal}>
          Update Leave
        </Button>
        <Button size="sm" variant="primary" onClick={() => setIsApplyModalOpen(true)}>
          Apply Leave
        </Button>
      </div>
      <div className="tp-leave__summary">
        {/* Row 1: Jan - July (H1) */}
        <p className="tp-leave__period ds-strong ds-text-muted">Jan – June {displayYear}</p>
        <div className="tp-leave__stats">
          <StatTile
            label="Total Leave Balance"
            value={displayEntry ? (displayEntry.h1Balance ?? "-") : (leaveBalanceData?.totalBalance ?? "-")}
          />
          <StatTile
            tone="success"
            label="Leave Remaining"
            value={displayEntry ? (displayEntry.h1leavesRemaining ?? "-") : (leaveBalanceData?.leavesRemaining ?? "-")}
          />
          <StatTile
            tone="danger"
            label="Leave Availed"
            value={displayEntry ? (displayEntry.h1LeavesAvailed ?? "-") : (leaveBalanceData?.leavesAvailed ?? "-")}
          />
        </div>

        <p className="tp-leave__period ds-strong ds-text-muted">July – Dec {displayYear}</p>
        <div className="tp-leave__stats">
          <StatTile
            label="Total Leave Balance"
            value={displayEntry ? (displayEntry.h2Balance ?? "-") : (leaveBalanceData?.totalBalance ?? "-")}
          />
          <StatTile
            tone="success"
            label="Leave Remaining"
            value={displayEntry ? (displayEntry.h2leavesRemaining ?? "-") : (leaveBalanceData?.leavesRemaining ?? "-")}
          />
          <StatTile
            tone="danger"
            label="Leave Availed"
            value={displayEntry ? (displayEntry.h2LeavesAvailed ?? "-") : (leaveBalanceData?.leavesAvailed ?? "-")}
          />
        </div>
      </div>

      {isLoading ? (
        <Loader label="Loading leave history…" />
      ) : (
        <div className="tp-leave__scroll">
          <Table caption="Leave history" className="tp-leave__table">
            <thead>
              <tr>
                <th scope="col">Slots Affected</th>
                <th scope="col">Leave Deducted</th>
                <th scope="col">From Date</th>
                <th scope="col">To Date</th>
                <th scope="col">Applied On</th>
                <th scope="col">Reason</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {leaveDetailsData.length > 0 ? (
                leaveDetailsData.map((leave, index) => {
                  const startDate = new Date(leave.fromDate);
                  const endDate = new Date(leave.toDate);
                  endDate.setHours(23, 59, 59, 999); // Set to end of day

                  const getDateTime = (dateStr: string, timeStr: string) => {
                    if (!dateStr || !timeStr) return undefined;
                    const base = dateStr.includes("T")
                      ? dateStr.split("T")[0]
                      : dateStr.split(" ")[0];
                    return `${base}T${timeStr}`;
                  };

                  const fromDateTime = getDateTime(leave.fromDate, leave.startTime);
                  const toDateTime = getDateTime(leave.toDate, leave.endTime);

                  return (
                    <tr key={index}>
                      <td>{formatLeaveCountCell(leave.leaveDuration)}</td>
                      <td>{leave.leaveBalance || 0.0}</td>
                      <td>
                        {fromDateTime
                          ? `${formatDate(fromDateTime, "shortDate")}\n 
                          ${formatDate(
                              fromDateTime,
                              "timeOnly"
                            )}`
                          : "N/A"}
                      </td>
                      <td>
                        {toDateTime
                          ? `${formatDate(toDateTime, "shortDate")} \n 
                          ${formatDate(
                              toDateTime,
                              "timeOnly"
                            )}`
                          : "N/A"}
                      </td>
                      <td>
                        <div className="tp-leave__applied">
                        {(leave.isEmergencyLeave === true || leave.isEmergencyLeave === "true") && (
                            <Badge tone="accent">
last one-hour emergency leave request                            </Badge>
                          )}
                          <span>{formatDate(leave.createdOn || leave.createdAt, "dateTime")}</span>
                        
                        </div>
                      </td>
                      <td>{leave.reasons}</td>
                      <td>
                        <TeacherStatusBadge status={leave.leaveStatus?.type} label={leave.leaveStatus?.type || "N/A"} />
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7}>
                    <EmptyState title="No leave history found." />
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

      )}
      <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(!isModalOpen)} size="md" centered>
        <ModalHeader toggle={() => setIsModalOpen(!isModalOpen)}>Update Leave Balance</ModalHeader>
        <ModalBody className="tp-leave__form">
          <fieldset className="tp-leave__group">
            <legend className="tp-leave__period ds-strong">Jan – June {updateModalYear || '—'}</legend>
            <div className="tp-leave__fields">
              <Field label="Balance">
                {(control) => (
                  <TextInput
                    {...control}
                    type="number"
                    numeric
                    min={0}
                    step={0.5}
                    value={formData.h1balance}
                    onChange={(e) => setFormData({ ...formData, h1balance: e.target.value })}
                    placeholder="0"
                  />
                )}
              </Field>
              <Field label="Leave Remaining">
                {(control) => (
                  <TextInput
                    {...control}
                    type="number"
                    numeric
                    min={0}
                    step={0.5}
                    value={formData.h1leaveRemaining}
                    onChange={(e) => setFormData({ ...formData, h1leaveRemaining: e.target.value })}
                    placeholder="0"
                  />
                )}
              </Field>
              <Field label="Leave Availed">
                {(control) => (
                  <TextInput
                    {...control}
                    type="number"
                    numeric
                    min={0}
                    step={0.5}
                    value={formData.h1leaveAvailed}
                    onChange={(e) => setFormData({ ...formData, h1leaveAvailed: e.target.value })}
                    placeholder="0"
                  />
                )}
              </Field>
            </div>
          </fieldset>
          <fieldset className="tp-leave__group">
            <legend className="tp-leave__period ds-strong">July – Dec {updateModalYear || '—'}</legend>
            <div className="tp-leave__fields">
              <Field label="Balance">
                {(control) => (
                  <TextInput
                    {...control}
                    type="number"
                    numeric
                    min={0}
                    step={0.5}
                    value={formData.h2balance}
                    onChange={(e) => setFormData({ ...formData, h2balance: e.target.value })}
                    placeholder="0"
                  />
                )}
              </Field>
              <Field label="Leave Remaining">
                {(control) => (
                  <TextInput
                    {...control}
                    type="number"
                    numeric
                    min={0}
                    step={0.5}
                    value={formData.h2leaveRemaining}
                    onChange={(e) => setFormData({ ...formData, h2leaveRemaining: e.target.value })}
                    placeholder="0"
                  />
                )}
              </Field>
              <Field label="Leave Availed">
                {(control) => (
                  <TextInput
                    {...control}
                    type="number"
                    numeric
                    min={0}
                    step={0.5}
                    value={formData.h2leaveAvailed}
                    onChange={(e) => setFormData({ ...formData, h2leaveAvailed: e.target.value })}
                    placeholder="0"
                  />
                )}
              </Field>
            </div>
          </fieldset>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleAddLeave}>Submit</Button>
        </ModalFooter>
      </Modal>

      {/* Apply Leave Modal */}
      <Modal isOpen={isApplyModalOpen} toggle={() => setIsApplyModalOpen(!isApplyModalOpen)} size="md" centered>
        <ModalHeader toggle={() => setIsApplyModalOpen(!isApplyModalOpen)}>Apply Leave</ModalHeader>
        <ModalBody className="tp-leave__form">
          <Field label="Leave Type" required id="leaveType">
            {(control) => (
              <Select
                {...control}
                value={applyForm.leaveType}
                onChange={(e) => setApplyForm({ ...applyForm, leaveType: e.target.value })}
                options={LEAVE_TYPES.map((t) => ({ value: t, label: t }))}
              />
            )}
          </Field>
          <Field label="From" required>
            {(control) => (
              <div className="tp-leave__datetime">
                <TextInput
                  {...control}
                  type="date"
                  value={applyForm.fromDate}
                  onChange={(e) => setApplyForm({ ...applyForm, fromDate: e.target.value })}
                />
                <Select
                  aria-label="From Time"
                  value={applyForm.fromTime}
                  onChange={(e) => setApplyForm({ ...applyForm, fromTime: e.target.value })}
                  options={[{ value: '', label: 'From Time' }, ...TIME_OPTIONS.map((t) => ({ value: t, label: t }))]}
                />
              </div>
            )}
          </Field>
          <Field label="To" required>
            {(control) => (
              <div className="tp-leave__datetime">
                <TextInput
                  {...control}
                  type="date"
                  value={applyForm.toDate}
                  onChange={(e) => setApplyForm({ ...applyForm, toDate: e.target.value })}
                />
                <Select
                  aria-label="To Time"
                  value={applyForm.toTime}
                  onChange={(e) => setApplyForm({ ...applyForm, toTime: e.target.value })}
                  options={[{ value: '', label: 'To Time' }, ...TIME_OPTIONS.map((t) => ({ value: t, label: t }))]}
                />
              </div>
            )}
          </Field>
          <Field label="Reason" required id="reason">
            {(control) => (
              <Input
                {...control}
                type="textarea"
                rows={3}
                value={applyForm.reason}
                onChange={(e) => setApplyForm({ ...applyForm, reason: e.target.value })}
                placeholder="Enter reason for leave"
              />
            )}
          </Field>
          <DescriptionList
            items={[
              {
                label: 'No. of Days',
                value: calculatingDays ? (
                  <span className="ds-text-muted">Calculating...</span>
                ) : noOfDays != null ? (
                  <span className="tp-leave__days ds-strong ds-num">{noOfDays}</span>
                ) : (
                  <span className="ds-text-muted">Select From and To date & time</span>
                ),
              },
            ]}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setIsApplyModalOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleApplyLeave} disabled={submittingApply || noOfDays == null}>
            {submittingApply ? 'Applying...' : 'Apply'}
          </Button>
        </ModalFooter>
      </Modal>

    </div>
  );
};

export default LeaveDetails;
